import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Copy } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import ConfirmActionDialog from '../department-policies/ConfirmActionDialog';
import {
    migrateActorUsers,
    type ActorWithCount,
} from '../../services/api/actorService';
import { getActorDisplayName } from '../../utils/roleUtils';
import { useToast } from '../../hooks/use-toast';

interface MigrateActorUsersDialogProps {
    open: boolean;
    actors: ActorWithCount[];
    onOpenChange: (open: boolean) => void;
    onMigrated: () => Promise<void> | void;
}

interface MigrationResult {
    migratedCount: number;
    emails: string[];
    targetName: string;
}

export default function MigrateActorUsersDialog({
    open,
    actors,
    onOpenChange,
    onMigrated,
}: MigrateActorUsersDialogProps) {
    const { toast } = useToast();
    const [sourceId, setSourceId] = useState('');
    const [targetId, setTargetId] = useState('');
    const [migrating, setMigrating] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [result, setResult] = useState<MigrationResult | null>(null);

    useEffect(() => {
        if (!open) {
            setSourceId('');
            setTargetId('');
            setConfirmOpen(false);
            setResult(null);
        }
    }, [open]);

    const departmentActors = useMemo(
        () => actors.filter((a) => a.scope === 'department'),
        [actors]
    );
    const source = departmentActors.find((a) => a._id === sourceId);
    const target = departmentActors.find((a) => a._id === targetId);

    const canMigrate =
        !!source && !!target && sourceId !== targetId && !migrating;

    const handleMigrate = async () => {
        if (!canMigrate || !source || !target) return;
        setMigrating(true);
        try {
            const { migratedCount, emails } = await migrateActorUsers(
                sourceId,
                targetId
            );
            setResult({
                migratedCount,
                emails,
                targetName: getActorDisplayName(target),
            });
            await onMigrated();
        } catch {
            toast({
                title: 'Failed to migrate users',
                variant: 'destructive',
            });
        } finally {
            setMigrating(false);
        }
    };

    const emailList = result?.emails.join('\n') ?? '';

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(emailList);
            toast({ title: 'Email list copied to clipboard' });
        } catch {
            toast({ title: 'Failed to copy', variant: 'destructive' });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                {result ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>Migration complete</DialogTitle>
                            <DialogDescription>
                                {result.migratedCount} user
                                {result.migratedCount !== 1 ? 's' : ''} moved
                                to &quot;{result.targetName}&quot;.
                            </DialogDescription>
                        </DialogHeader>

                        {result.emails.length > 0 ? (
                            <div className="space-y-2 py-2">
                                <div className="flex items-center justify-between gap-2">
                                    <Label>Migrated user emails</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={handleCopy}
                                    >
                                        <Copy className="mr-1.5 h-3 w-3" />
                                        Copy
                                    </Button>
                                </div>
                                <Textarea
                                    readOnly
                                    rows={6}
                                    className="text-xs"
                                    value={emailList}
                                    onFocus={(e) => e.target.select()}
                                />
                            </div>
                        ) : (
                            <p className="py-2 text-sm text-muted-foreground">
                                The source role had no users.
                            </p>
                        )}

                        <DialogFooter>
                            <Button onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Migrate Users</DialogTitle>
                            <DialogDescription>
                                Move every user from one department role to
                                another. Users keep their current permissions,
                                and the migrated users&apos; emails are listed
                                afterwards.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <div className="space-y-1.5">
                                <Label>From</Label>
                                <Select
                                    value={sourceId}
                                    onValueChange={setSourceId}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select source role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {departmentActors.map((actor) => (
                                            <SelectItem
                                                key={actor._id}
                                                value={actor._id}
                                            >
                                                {getActorDisplayName(actor)} (
                                                {actor.userCount} user
                                                {actor.userCount !== 1
                                                    ? 's'
                                                    : ''}
                                                )
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex justify-center text-muted-foreground">
                                <ArrowRight className="h-4 w-4 rotate-90" />
                            </div>

                            <div className="space-y-1.5">
                                <Label>To</Label>
                                <Select
                                    value={targetId}
                                    onValueChange={setTargetId}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select target role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {departmentActors
                                            .filter((a) => a._id !== sourceId)
                                            .map((actor) => (
                                                <SelectItem
                                                    key={actor._id}
                                                    value={actor._id}
                                                >
                                                    {getActorDisplayName(actor)}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {source && (
                                <p className="text-xs text-muted-foreground">
                                    {source.userCount} user
                                    {source.userCount !== 1 ? 's' : ''} will be
                                    moved across all departments.
                                </p>
                            )}
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={migrating}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => setConfirmOpen(true)}
                                disabled={!canMigrate}
                            >
                                {migrating ? 'Migrating…' : 'Migrate'}
                            </Button>
                        </DialogFooter>

                        <ConfirmActionDialog
                            open={confirmOpen}
                            onOpenChange={setConfirmOpen}
                            title="Migrate all users?"
                            description={`This will move ${source?.userCount ?? 0} user(s) from "${source ? getActorDisplayName(source) : ''}" to "${target ? getActorDisplayName(target) : ''}" across all departments. This cannot be undone from this dialog.`}
                            confirmLabel="Migrate"
                            onConfirm={() => {
                                setConfirmOpen(false);
                                handleMigrate();
                            }}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
